
import { supabase } from '../client';
import { toManualUserId } from '@/types';

export interface GameHoleBirdieRecord {
  id: string;
  userId: string;
  userName: string;
  userImageUrl: string | null;
  teeBox: string;
  scoreType: string;
  date: string;
  notes: string | null;
  enteredByUserId: string | null;
}

/**
 * Get all birdies for a specific hole within one or more games, including player profile info.
 * Uses a single batch query on score_games instead of N sequential queries.
 */
export const getGameHoleBirdies = async ({
  gameId,
  gameIds,
  courseId,
  holeNumber,
}: {
  gameId?: string;
  gameIds?: string[];
  courseId: string;
  holeNumber: number;
}): Promise<GameHoleBirdieRecord[]> => {
  const idsToQuery = gameIds && gameIds.length > 0 ? gameIds : gameId ? [gameId] : [];
  if (idsToQuery.length === 0) return [];

  // Batch query: get all score IDs for all games in one query
  const { data: scoreGameRows, error: sgError } = await supabase
    .from('score_games')
    .select('score_id')
    .in('game_id', idsToQuery);

  if (sgError) {
    console.error('Error fetching score_games batch:', sgError);
    return [];
  }

  const scoreIds = [...new Set((scoreGameRows || []).map(r => r.score_id))];
  if (scoreIds.length === 0) return [];

  // Fetch game date ranges to filter out incorrectly-linked scores from other years
  const { data: gameRows } = await supabase
    .from('games')
    .select('start_date, end_date')
    .in('id', idsToQuery);

  // Build the widest date window across all queried games
  let minDate: string | null = null;
  let maxDate: string | null = null;
  if (gameRows && gameRows.length > 0) {
    for (const g of gameRows) {
      if (g.start_date && (!minDate || g.start_date < minDate)) minDate = g.start_date;
      if (g.end_date && (!maxDate || g.end_date > maxDate)) maxDate = g.end_date;
    }
  }

  // Fetch scores for this hole with profile joins
  let query = supabase
    .from('scores')
    .select('id, user_id, tee, score_type, score_date, notes, hole_number, course_id, manual_player_id, entered_by_user_id, profiles:user_id(name, image_url), manual_players:manual_player_id(id, name)')
    .in('id', scoreIds)
    .eq('course_id', courseId)
    .eq('hole_number', holeNumber);

  if (minDate) query = query.gte('score_date', minDate);
  if (maxDate) query = query.lte('score_date', maxDate);

  const { data, error } = await query.order('score_date', { ascending: false });

  if (error) {
    console.error('Error fetching game hole birdies:', error);
    return [];
  }

  if (!data) return [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (data as any[]).map((score) => {
    const manualPlayer = score.manual_players as { id: string; name: string } | null;
    const effectiveUserId = manualPlayer ? toManualUserId(manualPlayer.id) : score.user_id;
    const effectiveName = manualPlayer ? manualPlayer.name : (score.profiles?.name || 'Unknown');
    const effectiveImageUrl = manualPlayer ? null : (score.profiles?.image_url || null);

    return {
      id: score.id,
      userId: effectiveUserId,
      userName: effectiveName,
      userImageUrl: effectiveImageUrl,
      teeBox: score.tee,
      scoreType: score.score_type,
      date: score.score_date?.split('T')[0] || '',
      notes: score.notes,
      enteredByUserId: score.entered_by_user_id || null,
    };
  });
};
